"use client";

import { useRef } from "react";
import { type Control, type FieldValues, type Path } from "react-hook-form";
import { FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import { Upload, X, ImageIcon } from "lucide-react";

interface FileUploadFieldProps<T extends FieldValues> {
  control: Control<T>;
  name: Path<T>;
  label: string;
  description?: string;
  disabled?: boolean;
  /** Allowed file types, e.g. "image/*" or "image/jpeg,image/png" */
  accept?: string;
}

export function FileUploadField<T extends FieldValues>({
  control,
  name,
  label,
  description,
  disabled = false,
  accept = "image/*",
}: FileUploadFieldProps<T>) {
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <FormField
      control={control}
      name={name}
      render={({ field }) => {
        // field.value is either "" (empty), a data-URL (base64), or an https URL (existing image)
        const currentValue = field.value as string;
        const hasPreview = !!currentValue;
        const isExistingUrl = currentValue.startsWith("http");

        function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
          const file = e.target.files?.[0];
          if (!file) return;

          const reader = new FileReader();
          reader.onload = () => {
            field.onChange(reader.result as string);
          };
          reader.readAsDataURL(file);
        }

        function clearFile() {
          field.onChange("");
          if (inputRef.current) inputRef.current.value = "";
        }

        return (
          <FormItem>
            <FormLabel>{label}</FormLabel>
            <FormControl>
              <div className="space-y-3">
                {/* Hidden native input */}
                <input
                  ref={inputRef}
                  type="file"
                  accept={accept}
                  disabled={disabled}
                  className="sr-only"
                  onChange={handleFileChange}
                  aria-label={label}
                />

                {/* Preview area */}
                {hasPreview ? (
                  <div className="relative inline-block">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={currentValue}
                      alt={label}
                      className="max-h-40 rounded-lg border object-contain"
                      style={{ borderColor: "hsl(240 20% 20%)" }}
                    />
                    {!disabled && (
                      <button
                        type="button"
                        onClick={clearFile}
                        className="absolute -right-2 -top-2 flex size-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-sm"
                        aria-label="Remove image"
                      >
                        <X className="size-3" />
                      </button>
                    )}
                    {isExistingUrl && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Current image — choose a new file to replace
                      </p>
                    )}
                  </div>
                ) : (
                  /* Upload zone */
                  <div
                    className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed px-6 py-8 transition-colors hover:border-orange-500/60 hover:bg-orange-500/5"
                    style={{ borderColor: "hsl(240 20% 25%)" }}
                    onClick={() => !disabled && inputRef.current?.click()}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onKeyDown={(e) => {
                      if (!disabled && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        inputRef.current?.click();
                      }
                    }}
                    aria-label={`Upload ${label}`}
                  >
                    <ImageIcon className="size-8 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Click to choose a file
                    </p>
                  </div>
                )}

                {/* Change button when preview is shown */}
                {hasPreview && !disabled && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => inputRef.current?.click()}
                  >
                    <Upload className="mr-2 size-3.5" />
                    Change image
                  </Button>
                )}
              </div>
            </FormControl>
            {description && <FormDescription>{description}</FormDescription>}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
